import React, { useState, useEffect } from 'react';
import { programAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import { FiPlus, FiX, FiTarget, FiDollarSign, FiUsers, FiCalendar } from 'react-icons/fi';

const sBtn = (bg='#3b82f6') => ({ padding: '10px 20px', background: bg, color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'Inter,sans-serif', display: 'inline-flex', alignItems: 'center', gap: 6 });
const sInput = { padding: '10px 14px', border: '2px solid #e2e8f0', borderRadius: 8, fontSize: 14, outline: 'none', fontFamily: 'Inter,sans-serif', width: '100%' };
const sLabel = { display: 'block', fontSize: 13, fontWeight: 600, color: '#475569', marginBottom: 4 };

const statusColor = { planning: '#8b5cf6', active: '#10b981', paused: '#f59e0b', completed: '#3b82f6', cancelled: '#ef4444' };

const Programs = () => {
  const { canEdit } = useAuth();
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDetail, setShowDetail] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', category: 'vaccination', status: 'planning', startDate: '', endDate: '', targetPopulation: '', targetRegions: [] });

  useEffect(() => {
    programAPI.getAll({ limit: 50 }).then(r => { setPrograms(r.data.programs || []); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await programAPI.create(form);
      toast.success('Program created');
      setShowModal(false);
      const r = await programAPI.getAll({ limit: 50 });
      setPrograms(r.data.programs || []);
    } catch (e) { toast.error('Failed'); }
  };

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}><div style={{ width: 40, height: 40, border: '4px solid #e2e8f0', borderTopColor: '#3b82f6', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} /></div>;

  return (
    <div>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div><h1 className="page-title" style={{ fontSize: 26, fontWeight: 800 }}>Community Health Programs</h1><p style={{ color: '#64748b', marginTop: 4 }}>Manage vaccination drives, screening camps, and wellness programs</p></div>
        {canEdit && <button onClick={() => setShowModal(true)} style={sBtn()}><FiPlus size={16}/> New Program</button>}
      </div>

      <div className="program-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(380px, 1fr))', gap: 20 }}>
        {programs.map(p => {
          const budgetPct = p.budget?.allocated ? Math.round((p.budget.spent / p.budget.allocated) * 100) : 0;
          const enrollPct = p.enrollment?.target ? Math.round((p.enrollment.current / p.enrollment.target) * 100) : 0;
          return (
            <div key={p._id} style={{ background: '#fff', borderRadius: 16, padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.06)', border: '1px solid #f1f5f9', cursor: 'pointer' }} onClick={() => setShowDetail(p)}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 14 }}>
                <span style={{ padding: '4px 12px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: `${statusColor[p.status]}20`, color: statusColor[p.status], textTransform: 'capitalize' }}>{p.status}</span>
                <span style={{ padding: '4px 12px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: '#f1f5f9', color: '#64748b', textTransform: 'capitalize' }}>{p.category?.replace('_', ' ')}</span>
              </div>
              <h3 style={{ fontSize: 17, fontWeight: 700, marginBottom: 8 }}>{p.name}</h3>
              <p style={{ fontSize: 13, color: '#64748b', marginBottom: 16, lineHeight: 1.5 }}>{p.description?.substring(0, 100)}{p.description?.length > 100 ? '...' : ''}</p>

              {/* Progress Bars */}
              <div style={{ marginBottom: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
                  <span style={{ color: '#64748b' }}><FiUsers size={12} style={{ marginRight: 4 }}/> Enrollment</span>
                  <span style={{ fontWeight: 600 }}>{(p.enrollment?.current||0).toLocaleString()} / {(p.enrollment?.target||0).toLocaleString()}</span>
                </div>
                <div style={{ height: 6, background: '#f1f5f9', borderRadius: 3 }}><div style={{ height: '100%', width: `${Math.min(enrollPct, 100)}%`, background: '#10b981', borderRadius: 3, transition: 'width 0.3s' }} /></div>
              </div>

              <div style={{ marginBottom: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
                  <span style={{ color: '#64748b' }}><FiDollarSign size={12} style={{ marginRight: 4 }}/> Budget</span>
                  <span style={{ fontWeight: 600 }}>${(p.budget?.spent||0).toLocaleString()} / ${(p.budget?.allocated||0).toLocaleString()}</span>
                </div>
                <div style={{ height: 6, background: '#f1f5f9', borderRadius: 3 }}><div style={{ height: '100%', width: `${Math.min(budgetPct, 100)}%`, background: budgetPct > 90 ? '#ef4444' : '#3b82f6', borderRadius: 3, transition: 'width 0.3s' }} /></div>
              </div>

              <div style={{ display: 'flex', gap: 16, fontSize: 12, color: '#94a3b8', marginTop: 14, borderTop: '1px solid #f1f5f9', paddingTop: 14 }}>
                <span><FiCalendar size={12} style={{ marginRight: 4 }}/>{p.startDate ? new Date(p.startDate).toLocaleDateString() : '-'}</span>
                <span>→ {p.endDate ? new Date(p.endDate).toLocaleDateString() : '-'}</span>
                {p.targetRegions?.length > 0 && <span>{p.targetRegions.join(', ')}</span>}
              </div>
            </div>
          );
        })}
      </div>

      {/* Detail Modal */}
      {showDetail && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}>
          <div className="modal-content" style={{ background: '#fff', borderRadius: 16, padding: 32, width: '100%', maxWidth: 700, maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
              <h2 style={{ fontSize: 22, fontWeight: 800 }}>{showDetail.name}</h2>
              <button onClick={() => setShowDetail(null)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><FiX size={22}/></button>
            </div>
            <p style={{ color: '#64748b', marginBottom: 20 }}>{showDetail.description}</p>

            {/* Goals */}
            {showDetail.goals?.length > 0 && (
              <div style={{ marginBottom: 20 }}>
                <h4 style={{ fontSize: 15, fontWeight: 700, marginBottom: 12 }}>Goals</h4>
                {showDetail.goals.map((g, i) => (
                  <div key={i} style={{ padding: 14, background: '#f8fafc', borderRadius: 10, marginBottom: 8 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}><span style={{ fontSize: 13, fontWeight: 500 }}>{g.description}</span><span style={{ fontWeight: 700, color: '#3b82f6' }}>{g.currentValue} / {g.targetValue} {g.unit}</span></div>
                    <div style={{ height: 6, background: '#e2e8f0', borderRadius: 3 }}><div style={{ height: '100%', width: `${Math.min((g.currentValue/g.targetValue)*100, 100)}%`, background: '#10b981', borderRadius: 3 }} /></div>
                  </div>
                ))}
              </div>
            )}

            {/* Milestones */}
            {showDetail.milestones?.length > 0 && (
              <div>
                <h4 style={{ fontSize: 15, fontWeight: 700, marginBottom: 12 }}>Milestones</h4>
                {showDetail.milestones.map((m, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderBottom: '1px solid #f1f5f9' }}>
                    <div style={{ width: 28, height: 28, borderRadius: '50%', background: m.status === 'completed' ? '#dcfce7' : m.status === 'in_progress' ? '#fef3c7' : '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12 }}>{m.status === 'completed' ? '✓' : i + 1}</div>
                    <div style={{ flex: 1 }}><div style={{ fontSize: 14, fontWeight: 500 }}>{m.title}</div><div style={{ fontSize: 12, color: '#94a3b8' }}>Due: {m.dueDate ? new Date(m.dueDate).toLocaleDateString() : '-'}</div></div>
                    <span style={{ padding: '4px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: m.status === 'completed' ? '#dcfce7' : '#fef3c7', color: m.status === 'completed' ? '#166534' : '#92400e', textTransform: 'capitalize' }}>{m.status}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Create Modal */}
      {showModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}>
          <div className="modal-content" style={{ background: '#fff', borderRadius: 16, padding: 32, width: '100%', maxWidth: 560, maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
              <h2 style={{ fontSize: 20, fontWeight: 800 }}>Create Health Program</h2>
              <button onClick={() => setShowModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><FiX size={22}/></button>
            </div>
            <form onSubmit={handleSubmit}>
              <div style={{ display: 'grid', gap: 14 }}>
                <div><label style={sLabel}>Program Name*</label><input style={sInput} value={form.name} onChange={e=>setForm({...form,name:e.target.value})} required/></div>
                <div><label style={sLabel}>Description</label><textarea style={{ ...sInput, minHeight: 80 }} value={form.description} onChange={e=>setForm({...form,description:e.target.value})}/></div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div><label style={sLabel}>Category</label><select style={sInput} value={form.category} onChange={e=>setForm({...form,category:e.target.value})}>{['vaccination','screening','wellness','chronic_management','maternal','mental_health','nutrition','emergency','other'].map(c=><option key={c} value={c}>{c.replace('_',' ')}</option>)}</select></div>
                  <div><label style={sLabel}>Status</label><select style={sInput} value={form.status} onChange={e=>setForm({...form,status:e.target.value})}>{['planning','active','paused','completed'].map(s=><option key={s} value={s}>{s}</option>)}</select></div>
                  <div><label style={sLabel}>Start Date</label><input type="date" style={sInput} value={form.startDate} onChange={e=>setForm({...form,startDate:e.target.value})}/></div>
                  <div><label style={sLabel}>End Date</label><input type="date" style={sInput} value={form.endDate} onChange={e=>setForm({...form,endDate:e.target.value})}/></div>
                </div>
                <div><label style={sLabel}>Target Population</label><input style={sInput} value={form.targetPopulation} onChange={e=>setForm({...form,targetPopulation:e.target.value})} placeholder="e.g., Adults 65+"/></div>
              </div>
              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 24 }}>
                <button type="button" onClick={() => setShowModal(false)} style={{ ...sBtn('#e2e8f0'), color: '#475569' }}>Cancel</button>
                <button type="submit" style={sBtn()}>Create Program</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Programs;
